import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { ConfirmationOptions, useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { CUSTOMER_JOURNEYS } from '@/modules/Customer/constants'
import { createCustomerJourneyNote } from '@/modules/Customer/services'
import { CustomerJourneyNote } from '@/modules/Customer/types'
import { ModalProps } from '@/types/shared'
import { generatePathDatacentral, paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import qs from 'query-string'
import { type ChangeEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox, Dialog, GridHookForm } from 'vnetwork-ui'
import { markImportantJourneyNote } from '@/modules/Customer/utils/notes'
import { AddCustomerJourneyNoteSchema } from './schema'

interface HandleShowNavigatePayload {
    customer_journey_note_step: string
    setConfirmation: (options: Partial<ConfirmationOptions>) => void
    t: Function
    customer_id: number
    customer_journey_id: number
    contact_id?: number
}

export const handleNextStep = ({
    customer_journey_note_step,
    setConfirmation,
    t,
    customer_id,
    customer_journey_id,
    contact_id,
}: HandleShowNavigatePayload) => {
    switch (customer_journey_note_step) {
        case 'poc':
            setConfirmation({
                title: t('create_customer_poc'),
                description: t('create_customer_poc_desc'),
                open: true,
                onConfirm: () => {
                    const search = {
                        customer_id: customer_id || 0,
                        customer_journey_id: customer_journey_id || 0,
                        contact_id: contact_id || 0,
                    }

                    window.open(
                        `${generatePathDatacentral(paths.customerCreatePOC, {
                            id: customer_id,
                        })}?${qs.stringify(search)}`,
                        '_blank'
                    )
                },
            })
            return true
        case 'contract': {
            setConfirmation({
                title: t('create_contract'),
                description: t('contract_desc'),
                open: true,
                onConfirm: () => {
                    const search = {
                        customer_id: customer_id || 0,
                        customer_journey_id: customer_journey_id || 0,
                        contact_id: contact_id || 0,
                    }

                    window.open(
                        `${paths.createContract}?${qs.stringify(search)}`,
                        '_blank'
                    )
                },
            })
            return true
        }
        case 'contact': {
            setConfirmation({
                title: t('business-create'),
                description: t('business-description'),
                open: true,
                onConfirm: () => {
                    const customerId = customer_id || 0
                    window.open(
                        `${paths.businessAccountCreate}?customerId=${customerId}`,
                        '_blank'
                    )
                },
            })
            return true
        }

        default: {
            return false
        }
    }
}

export interface AddCustomerJourneyNoteProps extends ModalProps {
    customer_id: number
    customer_journey_id: number
    contact_id: number
    customer_journey_note_step_default?: string
    customer_journey_note_step_status?: string
}

type AddCustomerJourneyNoteForm = Pick<
    CustomerJourneyNote,
    'customer_journey_note_step' | 'customer_journey_note_step_status'
> &
    Partial<
        Pick<
            CustomerJourneyNote,
            'customer_journey_note' | 'customer_journey_notes_time'
        >
    >

const AddCustomerJourneyNote: React.FC<AddCustomerJourneyNoteProps> = ({
    onClose,
    customer_id,
    customer_journey_id,
    open,
    contact_id,
    customer_journey_note_step_default,
    customer_journey_note_step_status,
}) => {
    const { t } = useTranslation()
    const { t: tCustomer } = useTranslation('customer')
    const { setConfirmation } = useConfirmation()
    const [isImportant, setIsImportant] = useState(false)
    const markAsImportantLabel = tCustomer('mark-as-important')

    const formControl = useForm<AddCustomerJourneyNoteForm>({
        defaultValues: {
            customer_journey_note: '',
            customer_journey_note_step:
                customer_journey_note_step_default || 'searching',
            customer_journey_note_step_status:
                customer_journey_note_step_status || 'processing',
            customer_journey_notes_time: dayjs(),
        },
        resolver: yupResolver(AddCustomerJourneyNoteSchema),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: createCustomerJourneyNote,
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
            successMessage: t('create-success'),
            onSuccess: (data) => {
                onClose()

                handleNextStep({
                    customer_id,
                    customer_journey_id,
                    customer_journey_note_step:
                        data?.result?.customer_journey_note_step,
                    setConfirmation,
                    contact_id,
                    t,
                })
            },
        }
    )

    const handleCreate = async (values: AddCustomerJourneyNoteForm) => {
        const payloadValues = { ...values }
        if (isImportant && payloadValues.customer_journey_note) {
            payloadValues.customer_journey_note = markImportantJourneyNote(
                payloadValues.customer_journey_note
            )
        }

        mutateAsync({
            customer_id,
            customer_journey_id,
            params: {
                note: payloadValues,
            },
        })
    }

    // Get step label for display
    const stepLabel =
        CUSTOMER_JOURNEYS(t)?.find(
            (i) => i.value === customer_journey_note_step_default
        )?.label || customer_journey_note_step_default

    return (
        <Dialog title={t('add-note')} onClose={onClose} open={open}>
            {/* Show current step info */}
            <div className="mb-4 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-xs text-gray-500">
                    {t('current-step')}:
                </span>
                <span className="ml-2 text-sm font-medium text-gray-700">
                    {stepLabel}
                </span>
            </div>

            <div className="mb-4 flex items-center gap-2">
                <Checkbox
                    checked={isImportant}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setIsImportant(e.target.checked)
                    }
                />
                <button
                    type="button"
                    className="text-sm font-medium text-gray-700 select-none"
                    onClick={() => setIsImportant((current) => !current)}
                >
                    {markAsImportantLabel}
                </button>
            </div>

            <GridHookForm
                formControl={formControl}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'customer_journey_notes_time',
                            type: 'datepicker',
                            placeholder: t('select-time'),
                            label: t('execution_time'),
                        },
                        className: 'md:col-span-4 col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'customer_journey_note',
                            type: 'textarea',
                            placeholder: t('enter-note-content'),
                            label: t('note'),
                        },
                        className: 'md:col-span-4 col-span-1',
                    },
                ]}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('save'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                className="mt-4"
            />
        </Dialog>
    )
}

export default AddCustomerJourneyNote
